export type SpeakingFeedbackSource = 'local' | 'webspeech' | 'gemini';

export interface SpeakingFeedback {
  transcript?: string;
  fluency?: string;
  grammar?: string;
  vocab?: string;
  betterVersion?: string;
  source: SpeakingFeedbackSource;
}

export interface ThinkingHints {
  summary: string;
  exampleAnswer: string;      // 完整示例回答
  phrases: string[];          // 相关短语列表
  angles: string[];           // 保留兼容旧数据
  dense: string;
  source: SpeakingFeedbackSource;
}

export function buildLocalThinkingHints(question?: string, draft?: string): ThinkingHints {
  const merged = `${draft ?? ''} ${question ?? ''}`.trim();
  const base = merged || '你想说的要点';

  return {
    summary: base.slice(0, 60),
    exampleAnswer: `Based on "${base}", I would say... (需要 AI 生成完整回答)`,
    phrases: [],  // 等待 AI 生成有意义的短语
    angles: [],
    dense: `${base.slice(0, 80)}...`,
    source: 'local',
  };
}

export function buildLocalFeedback(question?: string, draft?: string): SpeakingFeedback {
  const base = draft?.trim() || question?.trim() || '录音内容';
  return {
    transcript: base,
    fluency: '保持句子长度 6–12 词，搭配连接词如 moreover, besides, as a result。',
    grammar: '注意时态一致，口语中用简单句+并列句即可，少用从句卡顿。',
    vocab: '选择 2–3 个主题相关词替换重复表达，比如 kind → considerate, nice → delightful。',
    betterVersion: `${base} (优化：加入具体例子和原因，让听感更流畅)。`,
    source: 'local',
  };
}

export function extractTextFromGemini(content: any): string {
  if (!content) return '';
  if (typeof content === 'string') return content;

  const textParts = Array.isArray(content)
    ? content
    : content.candidates ?? content.parts ?? content.contents ?? [];

  const flatten = (part: any): string => {
    if (!part) return '';
    if (typeof part === 'string') return part;
    if (typeof part.text === 'string') return part.text;
    if (part.content) return flatten(part.content);
    if (Array.isArray(part.parts)) return part.parts.map(flatten).join('\n');
    return '';
  };

  return textParts.map(flatten).join('\n').trim();
}

export function feedbackFromGeminiText(text: string): SpeakingFeedback {
  const normalized = text.trim();
  if (!normalized) return { source: 'gemini' };

  // 优先尝试解析 JSON 结构
  const jsonLike = normalized.replace(/^```json\s*/i, '').replace(/```$/i, '').trim();
  if (jsonLike.startsWith('{')) {
    try {
      const obj = JSON.parse(jsonLike);
      return {
        transcript: obj.transcript ?? obj.text ?? obj.content,
        fluency: obj.fluency,
        grammar: obj.grammar,
        vocab: obj.vocab,
        betterVersion: obj.betterVersion ?? obj.better,
        source: 'gemini',
      };
    } catch (e) {
      // Fall through to line-based parsing
    }
  }

  const slices = normalized.split(/\n+/).map((line) => line.trim());
  const transcript = slices.find((line) => /^transcript[:：]/i.test(line))?.replace(/^transcript[:：]\s*/i, '');
  const fluency = slices.find((line) => /^fluency[:：]/i.test(line))?.replace(/^fluency[:：]\s*/i, '');
  const grammar = slices.find((line) => /^grammar[:：]/i.test(line))?.replace(/^grammar[:：]\s*/i, '');
  const vocab = slices.find((line) => /^vocab[:：]/i.test(line))?.replace(/^vocab[:：]\s*/i, '');
  const better =
    slices.find((line) => /betterVersion[:：]/i.test(line))?.replace(/betterVersion[:：]\s*/i, '') ||
    slices.find((line) => /^better[:：]/i.test(line))?.replace(/^better[:：]\s*/i, '');

  const fallback = slices.slice(0, 3).join(' ');

  return {
    transcript: transcript || fallback,
    fluency,
    grammar,
    vocab,
    betterVersion: better,
    source: 'gemini',
  };
}
